/**
 * The arithmetic and the rules behind "Add to downline".
 *
 * Every cap on this form is measured against something the parent actually
 * holds — their balance, their share, their commission, their bet ceiling — so
 * the rules are worth pinning in tests rather than leaving inline in a 900-line
 * component. The wizard that this replaces had all of it inline; nothing here
 * is new logic, it is the same logic made testable.
 *
 * Nothing in this file formats for the rail or the review card except the error
 * strings, which quote figures through `src/lib/format` like every other money
 * string in the console.
 */

import { money, pct } from '@/lib/format'
import type { Account, GameSwitches, Role } from '@/data/types'

/** Three to twenty characters, lowercase, starting with a letter. */
export const USERNAME_RE = /^[a-z][a-z0-9._]{2,19}$/

/**
 * The worked example on the rail. A share of "12%" is abstract; "₹12,000 of a
 * ₹1,00,000 loss" is not, and an operator setting terms is thinking in rupees.
 * One lakh because it is the round number this business talks in.
 */
export const ILLUSTRATION_BASE = 100_000

export type Availability = 'idle' | 'checking' | 'free' | 'taken'

/** Every input on the form, held as typed. Numbers stay strings until parsed. */
export interface CreateValues {
  role: Role | null
  username: string
  displayName: string
  comments: string
  creditLimit: string
  share: string
  fancy: string
  bookmaker: string
  matchOdds: string
  minBet: string
  maxBet: string
  games: GameSwitches
}

/** The only facts about the creator that this form needs — every cap comes from one of them. */
export type ParentTerms = Pick<Account, 'balance' | 'share' | 'commission' | 'betLimits'>

/* ------------------------------------------------------------------ parse -- */

/**
 * A blank box is not a zero — anything unparseable comes back null so the
 * caller can say "enter a number" rather than silently creating a ₹0 limit.
 */
export function parseNumber(text: string): number | null {
  const cleaned = text.replace(/[₹,%\s]/g, '')
  if (cleaned === '') return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}

/* ------------------------------------------------------------- validation -- */

/** Format only — the half of the username rule that does not need the network. */
export function usernameFormatError(username: string): string | null {
  const trimmed = username.trim()
  if (trimmed === '') return 'Pick a username for the new account'
  return USERNAME_RE.test(trimmed)
    ? null
    : 'Three to twenty characters: lowercase letters, digits, dot or underscore, starting with a letter'
}

/**
 * Format, then the availability verdict.
 *
 * `checking` and `idle` are deliberately NOT errors. An in-flight probe blocks
 * the submit for a beat (the button says so) and a probe that failed outright
 * must not wedge the form — the server enforces uniqueness either way.
 */
export function usernameError(username: string, availability: Availability): string | null {
  const format = usernameFormatError(username)
  if (format) return format
  return availability === 'taken' ? `${username.trim()} is already taken` : null
}

export function creditError(value: number | null, balance: number): string | null {
  if (value === null) return 'Enter a credit limit, or 0 to start them with none'
  if (value < 0) return 'A credit limit cannot be negative'
  if (value > balance) return `That is more than the ${money(balance)} you hold`
  return null
}

/**
 * The hundred points of a member's loss are split down the whole chain, so a
 * child's share is NOT carved out of the creator's — each level keeps its own
 * points. What the creator can hand down is therefore everything except the
 * slice they keep themselves.
 */
export function shareError(value: number | null, yourShare: number): string | null {
  const max = maxShareOf(yourShare)
  if (value === null) return 'Enter a share'
  if (value < 0) return 'A share cannot be negative'
  if (value > max) return `You keep ${pct(yourShare, 2)}, so at most ${pct(max, 2)} can sit below you`
  return null
}

export function maxShareOf(yourShare: number): number {
  return Math.max(0, 100 - yourShare)
}

export function commissionError(value: number | null, max: number, label: string): string | null {
  if (value === null) return `Enter a ${label} commission, or 0`
  if (value < 0) return 'Commission cannot be negative'
  if (value > max) {
    return `Your own ${label} commission is ${pct(max, 2)} — you cannot pay down more than you receive`
  }
  return null
}

/**
 * An agent carries no bet limits of its own (they are a member-level setting),
 * so a zero on the parent means "no ceiling to inherit", not "nothing allowed".
 */
export function parentMaxBet(parent: Pick<Account, 'betLimits'>): number | null {
  return parent.betLimits.max > 0 ? parent.betLimits.max : null
}

export interface BetLimitErrors {
  min: string | null
  max: string | null
}

/** Member-only. The caller decides whether to ask at all; this decides whether the pair coheres. */
export function betLimitErrors(
  min: number | null,
  max: number | null,
  parentMax: number | null,
): BetLimitErrors {
  const minError =
    min === null
      ? 'Enter a minimum bet'
      : min <= 0
        ? 'The minimum bet must be more than zero'
        : null

  const maxError =
    max === null
      ? 'Enter a maximum bet'
      : min !== null && max <= min
        ? 'The maximum bet must be above the minimum'
        : parentMax !== null && max > parentMax
          ? `Your own maximum is ${money(parentMax)}`
          : null

  return { min: minError, max: maxError }
}

/* ---------------------------------------------------------------- summary -- */

export interface CreateSummary {
  /** Parsed credit limit, or null while the box is blank. A blank is not a zero. */
  creditLimit: number | null
  /** Credit clamped into what the parent actually holds — for the bar only. */
  allocating: number
  remaining: number
  overAllocated: boolean
  yourShare: number
  /** Their share clamped into [0, maxShare] — the bar always shows a real hundred-point split. */
  theirShare: number
  restOfChain: number
  maxShare: number
  illustration: { base: number; toYou: number; toThem: number }
}

/**
 * The live consequence of what has been typed, in one object.
 *
 * Everything is clamped for display: an out-of-range figure is reported by the
 * field error, never by a bar that silently rescales itself around an
 * impossible number. That is why the illustration uses the clamped share — a
 * worked rupee figure derived from a rejected input would be a lie with a
 * decimal point on it.
 */
export function deriveSummary(
  input: Pick<CreateValues, 'creditLimit' | 'share'>,
  parent: ParentTerms,
): CreateSummary {
  const creditLimit = parseNumber(input.creditLimit)
  const shareValue = parseNumber(input.share)

  const allocating = creditLimit === null ? 0 : Math.max(0, Math.min(creditLimit, parent.balance))
  const remaining = Math.max(0, parent.balance - allocating)
  const overAllocated = creditLimit !== null && creditLimit > parent.balance

  const yourShare = parent.share.sportsbook
  const maxShare = maxShareOf(yourShare)
  const theirShare = Math.min(Math.max(shareValue ?? 0, 0), maxShare)
  const restOfChain = Math.max(0, 100 - yourShare - theirShare)

  return {
    creditLimit,
    allocating,
    remaining,
    overAllocated,
    yourShare,
    theirShare,
    restOfChain,
    maxShare,
    illustration: {
      base: ILLUSTRATION_BASE,
      toYou: (ILLUSTRATION_BASE * yourShare) / 100,
      toThem: (ILLUSTRATION_BASE * theirShare) / 100,
    },
  }
}

/** `4 of 5 enabled` for the rail, and the names behind it. */
export function enabledGames(games: GameSwitches, labels: Record<keyof GameSwitches, string>) {
  const keys = Object.keys(labels) as (keyof GameSwitches)[]
  const on = keys.filter((key) => games[key])
  return { on: on.length, total: keys.length, names: on.map((key) => labels[key]) }
}
